import { bufferToHexString } from '@iotize/common/byte-converter';
import { promiseSerial } from '@iotize/common/promise';
import { QueueComProtocol } from '@iotize/tap/protocol/core';

import { BleConfig } from './ble-config';
import { BLEPacketBuilder } from './ble-packet-builder';
import { BLEPacketSplitter } from './ble-packet-splitter';
import { debug } from './debug';
import { BleComError, InvalidBleFrameChecksum } from './errors';

/**
 * @deprecated use UniversalBleProtocolAdpater
 * Abstract class for BLE communication
 *
 * With ble communication, data is split into sub packets.
 * This class handles creation of packet chunks.
 *
 * - You must only implement the function to send one packet chunk writeLwm2mPacketChunk()
 * -
 */
export abstract class AbstractBleProtocol extends QueueComProtocol {
  // TODO set an option instead ?
  public static RECEIVED_BUFFER_LENGTH = 255;

  protected packetBuilder?: BLEPacketBuilder;
  protected packetSplitter?: BLEPacketSplitter;

  protected _readReject?: (reason?: any) => void;
  protected _pendingRead?: Uint8Array;
  protected _readResolve?: (
    value: Uint8Array | PromiseLike<Uint8Array>,
  ) => void;
  protected _readPromise?: Promise<Uint8Array>;

  /**
   * Actual implementation to send a packet chunk with ble
   */
  abstract writeLwm2mPacketChunk(data: Uint8Array): Promise<Uint8Array>;

  /**
   * Actual implementation to listen ble messages coming from LWM2M IoTize device
   */
  abstract subscribeToLwm2mMessage(): void;

  constructor(
    public bleOptions: { mtu: number } = {
      mtu: BleConfig.maxPacketLengthWithoutOffset + 1,
    },
  ) {
    super();
  }

  onNewLwm2mMessage(data: Uint8Array): void {
    if (this._readResolve) {
      debug('onNewLwm2mMessage()', bufferToHexString(data));
      this._readResolve(data);
    } else {
      console.warn(
        `No listener for new message... 0x${bufferToHexString(data)}`,
      );
      this._pendingRead = data;
    }
  }

  onNewLwm2mMessageError(err: Error): void {
    console.error('Error lwm2m response...', err);
  }

  onLwm2mDataChunkError(error: BleComError): void {
    if (this._readReject) {
      this._readReject(error);
    } else {
      console.warn('No listener for read message error...', error);
    }
  }

  getLwm2mResponsePromise() {
    debug('getLwm2mResponsePromise');
    if (this._pendingRead) {
      let data = this._pendingRead;
      this._pendingRead = undefined;
      return Promise.resolve(data);
    }
    if (!this._readPromise) {
      this.createLwm2mResponsePromise();
    }
    return this._readPromise!;
  }

  createLwm2mResponsePromise(): Promise<Uint8Array> {
    debug('createLwm2mResponsePromise');
    this._readPromise = new Promise<Uint8Array>((resolve, reject) => {
      this._readResolve = resolve;
      this._readReject = reject;
    });
    return this._readPromise;
  }

  // send(data: Uint8Array): Observable<Uint8Array> {
  //     debug("BLEProtocol:send() " + bufferToHexString(data));
  //     return from(
  //         this
  //             .write(data)
  //             .then(() => this.waitForLwm2mResponse())
  //             .catch((err) => {
  //                 console.error("ERROR SEND " + err);
  //                 throw err;
  //             })
  //     );
  // }

  read(): Promise<Uint8Array> {
    debug('read()...');
    if (this.packetBuilder) {
      if (this.packetBuilder.hasAllChunks()) {
        let data = this.packetBuilder.getData();
        this.packetBuilder = undefined;
        return Promise.resolve(data);
      }
      // TODO manage error
      console.warn(
        'AbstractBleProtocol::read() There is already a read operation in progress but a new call to ::read has been made.',
      );
    }
    return this.getLwm2mResponsePromise()
      .then((response: Uint8Array) => {
        this.packetBuilder = undefined;
        return response;
      })
      .catch((err) => {
        this.packetBuilder = undefined;
        throw err;
      });
  }

  async write(data: Uint8Array): Promise<any> {
    if (this.packetSplitter != null) {
      console.warn(
        'BLEProtocol',
        'write',
        'There are already data being sent but a new call to ::write has been made',
      );
    }
    this.packetSplitter = BLEPacketSplitter.wrapWithChecksum(
      data,
      this.bleOptions.mtu - 1,
    );
    let packets = this.packetSplitter.getPackets();

    let promise: Promise<any>;

    this._pendingRead = undefined;
    this.packetBuilder = new BLEPacketBuilder(
      AbstractBleProtocol.RECEIVED_BUFFER_LENGTH,
    );
    if (packets.length > 0) {
      // this.createLwm2mResponsePromise()
      //     .catch((err) => {
      //         this.onNewLwm2mMessageError(err);
      //     })
      promise = promiseSerial(packets, (packet: Uint8Array, index: number) => {
        return this.writeLwm2mPacketChunk(packet).then((value) => {
          debug(`Done sending packet ${index + 1}/${packets.length}`);
          return value;
        });
      });
      // packets.forEach((value: Uint8Array, index: number) => {
      //     if (!promise){
      //         promise = this.writeLwm2mPacketChunk(packets[index]);
      //     }
      //     else{
      //         promise = promise.then(() => this.writeLwm2mPacketChunk(packets[index]))
      //     }
      //     // promise.then(() => {
      //         // debug("BLEProtocol", "write", `Done sending packet ${index+1}/${packets.length}`);
      //     // });
      // });
    } else {
      console.warn('BLEProtocol', 'write', `Nothing to write...`);
      promise = Promise.resolve();
    }

    return promise
      .then(() => {
        this.packetSplitter = undefined;
      })
      .catch((err) => {
        this.packetSplitter = undefined;
        throw err;
      });
  }

  /**
   *
   * @param data data chunk
   */
  onNewLwm2mPacket(data: Uint8Array): void {
    if (!this.packetBuilder) {
      debug(
        `Ignoring lwm2m packet 0x${bufferToHexString(
          data,
        )} as not request has been made yet`,
      );
      return;
    }
    this.packetBuilder.append(data);

    if (this.packetBuilder.hasAllChunks()) {
      if (this.packetBuilder.isChecksumValid()) {
        this.onNewLwm2mMessage(this.packetBuilder.getData());
      } else {
        let error = InvalidBleFrameChecksum.create(this.packetBuilder);
        this.onLwm2mDataChunkError(error);
      }
    }
  }
}
